import type { AuditEvent } from "../../../api/types";

/**
 * The one audit-line humanizer (UX polish wave 3 — `AuditPage`'s "Actor"
 * column and Home's "Recent activity" line each hand-rolled their own
 * actor/action/resource formatting; A-5: fix once, both surfaces read it).
 * `actorUserId` is absent for system-initiated events (S-15) — those render
 * as "system", never a blank cell. The role suffix is included whenever the
 * server sent one, so both surfaces show the SAME level of detail about who
 * took the action.
 */
export function formatAuditActor(event: Pick<AuditEvent, "actorUserId" | "actorRole">): string {
  if (!event.actorUserId) return "system";
  return event.actorRole ? `${event.actorUserId} (${event.actorRole})` : event.actorUserId;
}

/**
 * A single plain-language line for an audit event — `actor — action ·
 * resource`, omitting the resource segment when the event carries none.
 * Built only from fields the event actually carries (A-10: no fabricated
 * detail); deliberately omits the raw `event.target` id, which is an opaque
 * identifier rather than something an operator reads at a glance. `AuditPage`
 * itself keeps that raw id available as its own mono "Target" column — this
 * formatter isn't a replacement for the trail's full record, just the
 * shared human-readable summary both surfaces need (Q-10: the audit trail's
 * own raw ids remain reachable, this just isn't where they live).
 */
export function formatAuditLine(event: Pick<AuditEvent, "actorUserId" | "actorRole" | "action" | "resource">): string {
  const actor = formatAuditActor(event);
  return event.resource ? `${actor} — ${event.action} · ${event.resource}` : `${actor} — ${event.action}`;
}
